// Hand-drawn Havana line-art motifs. All SVGs share the same stroke color so
// they blend seamlessly with the gold + beige theme.
// Usage: <Hibiscus className="absolute top-10 right-6 w-40 opacity-30" />

export function Hibiscus({ className = "", stroke = "currentColor" }) {
  return (
    <svg
      viewBox="0 0 200 200"
      fill="none"
      stroke={stroke}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Chinese hibiscus - five broad petals with ruffled edges */}
      <g transform="translate(100 100)">
        {[0, 72, 144, 216, 288].map((r) => (
          <g key={r} transform={`rotate(${r})`}>
            <path d="M0 -6 C 22 -46 48 -66 32 -92 C 18 -80 6 -66 -4 -50 C -12 -34 -8 -18 0 -6 Z" />
            <path d="M-2 -20 C 6 -42 22 -60 26 -74" />
          </g>
        ))}
        {/* Long curling stigma */}
        <path d="M0 0 C 4 22 -2 46 6 66 C 10 72 14 74 18 74" />
        {/* Pollen dots */}
        <circle cx="6" cy="66" r="1.6" fill={stroke} stroke="none" />
        <circle cx="12" cy="70" r="1.4" fill={stroke} stroke="none" />
        <circle cx="18" cy="74" r="1.6" fill={stroke} stroke="none" />
        {/* Center */}
        <circle cx="0" cy="0" r="4" />
        {/* Leaves */}
        <path d="M-70 30 C -50 40 -20 40 0 30" />
        <path d="M60 40 C 40 50 20 50 0 40" />
      </g>
    </svg>
  );
}

export function Frangipani({ className = "", stroke = "currentColor" }) {
  return (
    <svg
      viewBox="0 0 200 200"
      fill="none"
      stroke={stroke}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Frangipani (plumeria) - five overlapping pinwheel petals */}
      <g transform="translate(100 100)">
        {[0, 72, 144, 216, 288].map((r) => (
          <g key={r} transform={`rotate(${r})`}>
            <path d="M0 -4 C 28 -30 48 -50 22 -72 C 4 -60 -10 -40 -6 -20 C -4 -12 -2 -6 0 -4 Z" />
          </g>
        ))}
        <circle cx="0" cy="0" r="6" />
        <circle cx="0" cy="0" r="2.5" fill={stroke} stroke="none" />
        {/* Small bud on stem */}
        <path d="M60 60 C 74 66 84 80 82 96" />
        <path d="M82 96 C 88 92 92 84 88 76 C 82 82 78 90 82 96 Z" />
      </g>
    </svg>
  );
}

export function Cigar({ className = "", stroke = "currentColor" }) {
  return (
    <svg
      viewBox="0 0 220 120"
      fill="none"
      stroke={stroke}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Cigar body */}
      <path d="M20 78 C 40 60 160 60 190 74 C 196 76 198 82 190 84 C 160 96 40 96 20 82 C 16 80 16 80 20 78 Z" />
      {/* Band */}
      <path d="M150 68 L 150 90" />
      <path d="M172 66 L 172 90" />
      <path d="M150 74 L 172 74" />
      <path d="M150 82 L 172 82" />
      {/* Lit ember */}
      <path d="M18 80 C 14 80 12 78 12 76" />
      <circle cx="10" cy="80" r="2" fill={stroke} stroke="none" />
      {/* Smoke curls */}
      <path d="M14 60 C 20 48 8 40 16 28" />
      <path d="M28 56 C 34 42 22 32 30 20" />
      <path d="M44 52 C 50 40 40 30 46 18" />
    </svg>
  );
}

export function Mojito({ className = "", stroke = "currentColor" }) {
  return (
    <svg
      viewBox="0 0 140 200"
      fill="none"
      stroke={stroke}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Highball glass */}
      <path d="M30 30 L 40 168 C 40 176 46 182 54 182 L 86 182 C 94 182 100 176 100 168 L 110 30 Z" />
      {/* Rim */}
      <path d="M30 30 L 110 30" />
      {/* Liquid line */}
      <path d="M34 60 L 106 60" />
      {/* Ice cubes */}
      <path d="M46 80 L 56 84 L 54 96 L 44 92 Z" />
      <path d="M70 90 L 82 92 L 82 106 L 70 104 Z" />
      <path d="M52 108 L 62 112 L 60 124 L 50 120 Z" />
      {/* Mint leaves */}
      <path d="M60 20 C 56 8 46 4 40 8 C 42 16 50 24 60 20 Z" />
      <path d="M78 20 C 82 8 92 4 98 8 C 96 16 88 24 78 20 Z" />
      <path d="M68 18 C 68 6 74 2 80 2 C 78 12 74 18 68 18 Z" />
      {/* Straw */}
      <path d="M92 12 L 82 42" />
      <path d="M96 14 L 86 44" />
      {/* Lime wedge on rim */}
      <path d="M20 26 A 12 12 0 0 1 44 30" />
      <path d="M32 22 L 32 30" />
    </svg>
  );
}

// A composed background layer of scattered motifs. Use inside a relative
// container. Fixed positions so each section has its own configuration.
export function HavanaBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <Hibiscus className="absolute -top-6 -right-10 w-56 sm:w-72 text-[#D4AF37]/12 rotate-12" />
      <Frangipani className="absolute top-32 -left-16 w-56 sm:w-72 text-[#F3E08A]/10 -rotate-12" />
      <Cigar className="absolute bottom-16 right-10 w-56 sm:w-72 text-[#E7C98A]/12 rotate-6" />
      <Mojito className="absolute bottom-8 left-8 sm:left-16 w-32 sm:w-40 text-[#F3E08A]/14" />
    </div>
  );
}
